import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import { fetchSops, updateSop } from 'api/procurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import { DEFAULT_SOPS, SOP_SECTIONS } from '../constants'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  note: { marginBottom: theme.spacing(2) },
  block: { marginBottom: theme.spacing(3) },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  spacer: { flexGrow: 1 }
}))

// HQ editor for the per-category SOPs shown to centre teams via the (i) button
// on each form. One editable box per category; Save persists to the backend so
// the next centre that opens the (i) sees the new text.
const HqSop = () => {
  const classes = useStyles()
  const [drafts, setDrafts] = React.useState({})
  const [loading, setLoading] = React.useState(true)
  const [savingKey, setSavingKey] = React.useState('')

  React.useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const res = await fetchSops()
        const byCat = {}
        ;(res?.results || []).forEach((s) => {
          byCat[s.category] = s.content || ''
        })
        const next = {}
        SOP_SECTIONS.forEach((c) => {
          next[c.key] =
            byCat[c.key] !== undefined ? byCat[c.key] : DEFAULT_SOPS[c.key] || ''
        })
        setDrafts(next)
      } catch (error) {
        eventAlert(
          error?.message || 'Failed to load SOPs',
          constants.ERROR,
          'SOP'
        )
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const save = async (category) => {
    setSavingKey(category)
    try {
      await updateSop({ category, content: drafts[category] || '' })
      eventAlert('SOP saved', constants.SUCCESS, 'SOP')
    } catch (error) {
      eventAlert(error?.message || 'Failed to save SOP', constants.ERROR, 'SOP')
    } finally {
      setSavingKey('')
    }
  }

  if (loading) {
    return (
      <Box className={classes.root}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Typography variant="body2" color="textSecondary" className={classes.note}>
        These notes appear to centre teams when they click the ⓘ button on each
        form. Edit and save — changes are live immediately. Leave a blank line
        between paragraphs.
      </Typography>
      {SOP_SECTIONS.map((c) => (
        <Paper key={c.key} elevation={0} className={classes.block}>
          <div className={classes.head}>
            <Typography variant="subtitle1">
              <b>{c.label}</b>
            </Typography>
            <div className={classes.spacer} />
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={savingKey === c.key}
              onClick={() => save(c.key)}>
              {savingKey === c.key ? 'Saving…' : 'Save'}
            </Button>
          </div>
          <TextField
            multiline
            fullWidth
            minRows={6}
            variant="outlined"
            value={drafts[c.key] || ''}
            onChange={(e) =>
              setDrafts((prev) => ({ ...prev, [c.key]: e.target.value }))
            }
          />
        </Paper>
      ))}
    </Box>
  )
}

export default HqSop
